import _ from "lodash";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { useTranslation } from "react-i18next";
import { InputActionMeta } from "react-select";
import useEvent from "react-use-event-hook";

import {
  useDropdownSourceContext,
  usePageContext,
  useSelectItemsTranslate,
} from "@ryde/redux";
import {
  getGridLabelKey,
  IDropdownItem,
  ISelectListItem,
  Log,
  NO_VALUE_DISPLAY,
  normalizeToArrayValue,
  sortFilterDropdownArray,
  sortFiltersFnFactory,
  ValueType,
} from "@ryde/shared";

import { IFilterMultiReportsProps } from "./filter-multiselect.reports.models";
import { useTableFiltersActionsReports } from "./use-table-filters-actions-reports";

export const useFilterMultiselectReports = (
  props: IFilterMultiReportsProps,
) => {
  const { t } = useTranslation();
  const {
    templateKey,
    dropdownSource,
    selectItemsSource,
    initialCheckedValues,
    onValuesChange,
    isSingleValue,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { setValuesToFilter } = useTableFiltersActionsReports();
  const { getDropdownItems } = useDropdownSourceContext();
  const { entity } = usePageContext();
  const [input, setInput] = useState("");
  const { getTranslatedSelectList } = useSelectItemsTranslate();
  const [, startTransition] = useTransition();

  const [dropdownValue, setDropdownValue] = useState<ISelectListItem[]>([]);
  const valueValuesSet = useMemo(() => {
    return new Set(dropdownValue.map(({ value }) => value));
  }, [dropdownValue]);

  const [dropdownItems, setDropdownItems] = useState<ISelectListItem[]>([]);

  const multiselectLabel = useMemo((): string => {
    return t(getGridLabelKey(entity, templateKey));
  }, [entity, t, templateKey]);

  /**
   * Send the picked values to whichever store owns this filter — the reports
   * page's shared filter state by default, or the caller's own sink (the
   * dashboard writes them onto the card).
   */
  const applyValues = useEvent((values: number[] | string[]): void => {
    if (onValuesChange) {
      onValuesChange({ templateKey, values });
      return;
    }
    setValuesToFilter?.(templateKey, values);
  });

  const onItemsCheckChange = useCallback(
    (newValue: IDropdownItem[]): void => {
      if (newValue && templateKey) {
        Log.debug("Update filter value for field: " + templateKey, newValue);
        const picked = isSingleValue ? newValue.slice(-1) : newValue;
        setDropdownValue(picked);
        applyValues(
          (picked?.map(item => item.value) || []) as number[] | string[],
        );
      }
    },
    [applyValues, isSingleValue, templateKey],
  );

  const inputToFilter = useMemo(() => input.toLowerCase().trim(), [input]);
  const filteredDropDownItems = useMemo(
    () =>
      inputToFilter
        ? dropdownItems?.filter(
            ({ label, value }) =>
              String(label).toLowerCase().match(inputToFilter) ||
              String(value).toLowerCase().match(inputToFilter),
          )
        : dropdownItems,
    [dropdownItems, inputToFilter],
  );

  const isSelectedAll = filteredDropDownItems?.every(({ value }) =>
    valueValuesSet.has(value),
  );

  const selectAll = (): void => {
    if (!templateKey || isSingleValue) {
      return;
    }

    const filteredDropDownItemsValues = filteredDropDownItems.map(
      ({ value }) => value,
    );

    const includedFilteredDropdownItems = [
      ...dropdownValue,
      ...filteredDropDownItems,
    ];
    const filteredDropDownItemsValuesSet = new Set(filteredDropDownItemsValues);
    const excludedFilteredDropdownItems = dropdownValue.filter(
      ({ value }) => !filteredDropDownItemsValuesSet.has(value),
    );
    const includedFilteredDropdownItemsValues =
      includedFilteredDropdownItems.map(({ value }) => value);
    const excludedFilteredDropdownItemsValues =
      excludedFilteredDropdownItems.map(({ value }) => value);

    setDropdownValue(
      !isSelectedAll
        ? includedFilteredDropdownItems
        : excludedFilteredDropdownItems,
    );
    applyValues(
      !isSelectedAll
        ? (includedFilteredDropdownItemsValues as number[] | string[])
        : (excludedFilteredDropdownItemsValues as number[] | string[]),
    );
  };

  const loadFilterDropdownItems = useCallback(async () => {
    if (dropdownSource) {
      const items = await getDropdownItems?.(dropdownSource);

      if (!items || !_.isArray(items) || !items.length) {
        setDropdownItems([]);
      } else {
        const sortedItems = items.toSorted(sortFiltersFnFactory());
        setDropdownItems(sortedItems);
      }
    } else if (selectItemsSource?.length) {
      const translatedItems = getTranslatedSelectList(selectItemsSource);
      const sortedItems = translatedItems.toSorted(sortFiltersFnFactory());
      setDropdownItems(sortedItems);
    } else {
      Log.warn(t("errors.invalidDropdownSourceInFilter"));
      setDropdownItems([]);
    }
  }, [
    dropdownSource,
    getDropdownItems,
    getTranslatedSelectList,
    selectItemsSource,
    t,
  ]);

  useEffect(() => {
    if (!isOpen) {
      startTransition(() => {
        setDropdownItems(items =>
          sortFilterDropdownArray(items, dropdownValue),
        );
      });
    }

    /* sort items - put selected items on top while reopening dropdown (but not on selecting each item)*/
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const setInitialDropdownValues = useCallback(
    (items: ISelectListItem[], filterIds?: ValueType | null) => {
      if (filterIds && templateKey) {
        const initialDropdownValueItems = items.filter(v =>
          normalizeToArrayValue(filterIds).some(
            x => String(x) === String(v.value),
          ),
        );
        Log.debug(
          "Set initial value for template: " + templateKey,
          initialDropdownValueItems,
          filterIds,
        );
        setDropdownValue(initialDropdownValueItems);
      } else if (filterIds === null) {
        setDropdownValue([]);
      }
    },
    [templateKey],
  );
  useEffect(() => {
    if (templateKey) {
      startTransition(() => {
        loadFilterDropdownItems();
      });
    }
  }, [loadFilterDropdownItems, templateKey]);

  useEffect(() => {
    setInitialDropdownValues(dropdownItems, initialCheckedValues);
  }, [dropdownItems, initialCheckedValues, setInitialDropdownValues]);

  const buttonContent = useMemo(
    () => ({
      labelBlack: `${
        dropdownValue.length
          ? dropdownItems.find(item => item.value === dropdownValue[0].value)
              ?.label
          : NO_VALUE_DISPLAY
      }`,
      labelBlue: `${dropdownValue.length > 1 ? `+${dropdownValue.length - 1}` : ""}`,
    }),
    [dropdownValue, dropdownItems],
  );

  const onInputChange = useEvent(
    (inputValue: string, action: InputActionMeta): void => {
      if (action.action === "input-change") {
        setInput(inputValue);
      }
    },
  );

  return {
    filteredDropDownItems,
    isOpen,
    setIsOpen,
    value: dropdownValue,
    buttonContent,
    multiselectLabel,
    onItemsCheckChange,
    dropdownItems,
    selectAll,
    isSelectedAll,
    input,
    onInputChange,
  };
};
